import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, ThumbsUp, Plus, ArrowRight, Loader2, CheckCircle2, Award, Trash2 } from "lucide-react";
import ReportDialog from "@/components/ReportDialog";

const PROPOSAL_IMG = "https://images.unsplash.com/photo-1675116731363-c17d957f3444?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxzdXN0YWluYWJsZSUyMHRlY2hub2xvZ3klMjBncmVlbnxlbnwwfHx8fDE3ODYzNzA0MTR8MA&ixlib=rb-4.1.0&q=85";

export function ProposalsList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", idea: "", mode: "ai" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/proposals", { params: { status: tab === "all" ? undefined : tab } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const create = async () => {
    if (!user) return toast.error("Sign in first");
    if (form.title.length < 3 || form.idea.length < 10) return toast.error("Title and idea are required");
    setBusy(true);
    try {
      await api.post("/proposals", form);
      toast.success(form.mode === "ai" ? "AI refined your proposal" : "Proposal submitted");
      setOpen(false);
      setForm({ title: "", idea: "", mode: "ai" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const vote = async (id) => {
    if (!user) return toast.error("Sign in to vote");
    try {
      await api.post("/proposals/vote", { proposal_id: id });
      toast.success("Vote recorded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-16">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs text-primary">// 01 · proposals</div>
          <h1 className="mt-2 text-5xl font-black tracking-tighter">Refine an eco-idea.</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">Submit any product concept — the AI turns it into a real specification. Fully-formed ideas can skip the AI and go straight to community filtering. Reach 1000 upvotes to be sent to scientists.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-proposal-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Plus className="mr-2 h-4 w-4" />New proposal</Button></DialogTrigger>
          <DialogContent className="max-w-xl rounded-none">
            <DialogHeader><DialogTitle>Submit an idea</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Title</Label>
                <Input data-testid="proposal-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 rounded-none bg-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Describe the idea</Label>
                <Textarea data-testid="proposal-idea" rows={6} value={form.idea} onChange={(e) => setForm({ ...form, idea: e.target.value })} className="mt-2 rounded-none bg-input" placeholder="e.g. A modular insulation panel grown from mycelium that hooks into standard stud walls…" />
              </div>
              <div className="flex gap-2">
                <button data-testid="mode-ai" onClick={() => setForm({ ...form, mode: "ai" })} className={`flex-1 border p-4 text-left ${form.mode === "ai" ? "border-primary bg-primary/5" : "border-border"}`}>
                  <div className="flex items-center gap-2 text-sm font-bold"><Sparkles className="h-4 w-4 text-primary" /> AI refine</div>
                  <div className="mt-1 text-xs text-muted-foreground">Claude turns it into a spec: materials, cost, mechanism</div>
                </button>
                <button data-testid="mode-direct" onClick={() => setForm({ ...form, mode: "direct" })} className={`flex-1 border p-4 text-left ${form.mode === "direct" ? "border-primary bg-primary/5" : "border-border"}`}>
                  <div className="flex items-center gap-2 text-sm font-bold"><ArrowRight className="h-4 w-4 text-primary" /> Direct filter</div>
                  <div className="mt-1 text-xs text-muted-foreground">Already fully-formed — skip AI, community votes</div>
                </button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={busy} data-testid="submit-proposal" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
                {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Refining…</> : "Submit"}
              </Button>
            </DialogFooter>
            {busy && form.mode === "ai" && <div className="beam mt-2 h-1 bg-primary/10" />}
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-8">
        <TabsList className="rounded-none border border-border bg-transparent p-0">
          <TabsTrigger value="all" data-testid="tab-all" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">All</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Pending</TabsTrigger>
          <TabsTrigger value="filtered" data-testid="tab-filtered" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Filtered → scientists</TabsTrigger>
          <TabsTrigger value="implemented" data-testid="tab-implemented" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Verified success</TabsTrigger>
        </TabsList>
      </Tabs>

      {items.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <img src={PROPOSAL_IMG} alt="" className="mx-auto mb-6 h-40 w-40 border border-primary/40 object-cover grayscale" />
          <div className="text-lg font-bold">No proposals yet</div>
          <div className="mt-2 text-sm text-muted-foreground">Be first — a good idea, however raw, deserves an echo.</div>
        </div>
      ) : (
        <div className="grid gap-px bg-border md:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <div key={p.id} data-testid={`proposal-card-${p.id}`} className={`bg-background p-6 flex flex-col ${p.status === "implemented" ? "ring-1 ring-primary" : ""}`}>
              <div className="flex items-center justify-between text-xs">
                {p.status === "implemented" ? (
                  <Badge data-testid={`badge-verified-${p.id}`} className="rounded-none bg-primary text-primary-foreground uppercase tracking-widest text-[9px]">
                    <CheckCircle2 className="h-3 w-3 mr-1" />Verified Success
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-border rounded-none uppercase tracking-widest text-[9px]">{p.status}</Badge>
                )}
                <span className="mono text-primary">{p.mode === "ai" ? "AI-refined" : "direct"}</span>
              </div>
              <h3 className="mt-4 text-xl font-bold tracking-tight line-clamp-2">{p.title}</h3>
              <p className="mt-2 line-clamp-3 text-sm text-muted-foreground flex-1">{p.idea}</p>
              <div className="mt-4 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">by <Link to={`/u/${p.author_id}`} data-testid={`proposal-author-link-${p.author_id}`} onClick={(e) => e.stopPropagation()} className="text-primary hover:underline">{p.author_name}</Link></span>
                <div className="flex items-center gap-2">
                  <button data-testid={`vote-${p.id}`} onClick={() => vote(p.id)} className="flex items-center gap-1 border border-border px-2 py-1 hover:border-primary hover:text-primary">
                    <ThumbsUp className="h-3 w-3" /><span className="mono">{p.votes}</span>
                  </button>
                  <Link to={`/proposals/${p.id}`} className="border border-border px-2 py-1 hover:border-primary hover:text-primary uppercase tracking-widest text-[10px]">Open</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProposalDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [note, setNote] = useState("");
  const [co2, setCo2] = useState("");
  const [units, setUnits] = useState("");
  const [busy, setBusy] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const load = () => api.get(`/proposals/${id}`).then((r) => setP(r.data)).catch(() => nav("/proposals"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (!user) return;
    api.get("/admin/whoami").then((r) => setIsAdmin(!!r.data?.is_admin)).catch(() => {});
  }, [user]);

  const adminDelete = async () => {
    if (!window.confirm("Delete this proposal? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/proposal/${id}`);
      toast.success("Proposal deleted");
      nav("/proposals");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const markImplemented = async () => {
    setBusy(true);
    try {
      const payload = { proposal_id: id, company: user?.display_name, note };
      if (co2 !== "") payload.co2_saved_kg = parseFloat(co2);
      if (units !== "") payload.units_deployed = parseInt(units, 10);
      await api.post("/proposals/mark-implemented", payload);
      toast.success("Marked as Verified Success — +100 pts awarded to author");
      setNote(""); setCo2(""); setUnits("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!p) return <div className="mx-auto max-w-4xl px-6 py-24 text-muted-foreground">Loading…</div>;

  const r = p.ai_refined || {};
  const K = ({ k, children }) => (
    <div className="border border-border p-5">
      <div className="text-[10px] uppercase tracking-widest text-primary">{k}</div>
      <div className="mt-2 text-sm leading-relaxed">{children}</div>
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl px-6 py-16">
      <Link to="/proposals" className="mono text-xs text-muted-foreground hover:text-primary">← all proposals</Link>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tighter">{p.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span>by <Link to={`/u/${p.author_id}`} data-testid={`proposal-author-link-${p.author_id}`} className="text-primary hover:underline">{p.author_name}</Link></span><span className="text-primary">·</span>
            <span className="uppercase tracking-widest">{p.status}</span><span className="text-primary">·</span>
            <span className="mono">{p.votes} votes</span>
            {user && <ReportDialog targetType="proposal" targetId={p.id} testId={`report-proposal-${p.id}`} compact />}
            {isAdmin && (
              <button
                data-testid={`admin-delete-proposal-${p.id}`}
                onClick={adminDelete}
                className="flex items-center gap-1 border border-destructive/60 px-2 py-1 text-[10px] uppercase tracking-widest text-destructive hover:bg-destructive hover:text-destructive-foreground"
              >
                <Trash2 className="h-3 w-3" />Delete
              </button>
            )}
          </div>
        </div>
        {p.status === "implemented" && (
          <div data-testid="verified-success-panel" className="border border-primary bg-primary/10 p-4 max-w-sm">
            <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest">
              <CheckCircle2 className="h-4 w-4" />Verified Success
            </div>
            <div className="mt-2 text-sm">
              <Link to={`/companies-wall/${encodeURIComponent(p.implemented_by_company || p.implemented_by || "")}`} data-testid="verified-company-link" className="font-bold text-primary hover:underline">{p.implemented_by_company || p.implemented_by}</Link> marked this implemented.
            </div>
            {p.implemented_note && <div className="mt-2 text-xs text-muted-foreground italic">"{p.implemented_note}"</div>}
            {(p.co2_saved_kg || p.units_deployed) && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {p.co2_saved_kg != null && <div className="border border-primary/40 p-2"><div className="mono text-primary font-bold">{p.co2_saved_kg} kg</div><div className="text-[9px] uppercase tracking-widest text-muted-foreground">CO₂ saved</div></div>}
                {p.units_deployed != null && <div className="border border-primary/40 p-2"><div className="mono text-primary font-bold">{p.units_deployed}</div><div className="text-[9px] uppercase tracking-widest text-muted-foreground">units deployed</div></div>}
              </div>
            )}
            <div className="mt-2 mono text-xs text-primary">+100 pts → {p.author_name}</div>
          </div>
        )}
      </div>

      <div className="mt-8 border border-border p-6">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Original idea</div>
        <p className="mt-3 whitespace-pre-wrap leading-relaxed">{p.idea}</p>
      </div>
      {p.ai_refined && !r.raw && (
        <div className="mt-10">
          <div className="mb-4 inline-flex items-center gap-2 border border-primary/40 px-3 py-1 text-xs uppercase tracking-widest text-primary"><Sparkles className="h-3 w-3" /> AI-refined spec</div>
          <div className="grid gap-px bg-border sm:grid-cols-2">
            {r.summary && <K k="Summary">{r.summary}</K>}
            {r.target_user && <K k="Target user">{r.target_user}</K>}
            {r.functional_mechanism && <K k="Functional mechanism">{r.functional_mechanism}</K>}
            {r.estimated_cost && <K k="Estimated cost">{r.estimated_cost}</K>}
            {r.materials && <K k="Materials"><ul className="list-disc pl-4 space-y-1">{r.materials.map((m, i) => <li key={`${m}-${i}`}>{m}</li>)}</ul></K>}
            {r.manufacturing_steps && <K k="Manufacturing"><ol className="list-decimal pl-4 space-y-1">{r.manufacturing_steps.map((m, i) => <li key={`${m}-${i}`}>{m}</li>)}</ol></K>}
            {r.risks && <K k="Risks"><ul className="list-disc pl-4 space-y-1">{r.risks.map((m, i) => <li key={`${m}-${i}`}>{m}</li>)}</ul></K>}
            {r.sustainability_claims && <K k="Sustainability claims"><ul className="list-disc pl-4 space-y-1">{r.sustainability_claims.map((m, i) => <li key={`${m}-${i}`}>{m}</li>)}</ul></K>}
            {r.next_experiment && <K k="Next experiment">{r.next_experiment}</K>}
          </div>
        </div>
      )}
      {p.ai_refined && r.raw && (
        <div className="mt-8 border border-border p-6 whitespace-pre-wrap text-sm">{r.summary}</div>
      )}

      {user?.role === "company" && p.status !== "implemented" && (
        <div data-testid="mark-implemented-panel" className="mt-10 border border-primary/50 bg-primary/5 p-6">
          <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest">
            <Award className="h-4 w-4" />Verify implementation
          </div>
          <p className="mt-2 text-sm text-muted-foreground">Confirming that your company has implemented this idea awards the author 100 bonus points and stamps a Verified Success badge across the platform.</p>
          <Textarea data-testid="impl-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short note about the implementation (optional)…" className="mt-3 rounded-none bg-input" />
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">CO₂ saved (kg / year)</Label>
              <Input data-testid="impl-co2" type="number" min="0" step="0.1" value={co2} onChange={(e) => setCo2(e.target.value)} className="mt-1 rounded-none bg-input" placeholder="e.g. 1200" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Units deployed</Label>
              <Input data-testid="impl-units" type="number" min="0" step="1" value={units} onChange={(e) => setUnits(e.target.value)} className="mt-1 rounded-none bg-input" placeholder="e.g. 500" />
            </div>
          </div>
          <Button data-testid="mark-implemented-btn" onClick={markImplemented} disabled={busy} className="mt-4 rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
            <CheckCircle2 className="mr-2 h-4 w-4" />{busy ? "Verifying…" : "Mark as Verified Success (+100 pts)"}
          </Button>
        </div>
      )}
    </div>
  );
}
